import { useEffect, useState } from 'react';
import { aboutInfo } from '@/mocks/about';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: `关于我们 - ${aboutInfo.brandName}`,
      description: aboutInfo.slogan,
      url: 'https://baoyu.com/about',
      isPartOf: {
        '@type': 'WebSite',
        name: '鲍鱼网址域名导航',
        url: 'https://baoyu.com',
      },
    });
    document.head.appendChild(script);

    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: aboutInfo.brandName,
      url: 'https://baoyu.com',
      description: aboutInfo.introduction.substring(0, 150),
      contactPoint: {
        '@type': 'ContactPoint',
        email: aboutInfo.contact.email,
        contactType: 'customer service',
      },
    });
    document.head.appendChild(orgScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(orgScript);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9q4ijue3oq4jqumtg7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        {/* Brand Intro */}
        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className="ri-anchor-line text-3xl text-primary-600"></i>
                </div>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">{aboutInfo.brandName}</h1>
                <strong className="text-primary-500 text-base font-medium block mb-4">{aboutInfo.slogan}</strong>
                <div className="text-foreground-600 text-sm leading-relaxed space-y-4 whitespace-pre-line">
                  {aboutInfo.introduction}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-10">信誉数据</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutInfo.reputation.map((item) => (
                <div key={item.label} className="bg-background-50 rounded-lg p-6 text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{item.value}</div>
                  <div className="mt-2 text-sm text-foreground-500">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Info */}
        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-10">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                      <div className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">微信客服</div>
                      <div className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contact.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-qq-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">QQ客服</div>
                      <div className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contact.qq}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-500 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">工作时间</div>
                      <div className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contact.workingHours}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-100 rounded-lg p-6">
                <h3 className="font-heading font-semibold text-foreground-900 mb-4">发送消息</h3>
                {formStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                      <i className="ri-check-line text-xl"></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-800">消息已发送！</p>
                    <p className="text-xs text-foreground-500 mt-1">我们将尽快与您联系</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
                    <input
                      type="text"
                      name="phone_alt"
                      className="form-field-website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">消息</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      ></textarea>
                    </div>
                    {formStatus === 'error' && (
                      <p className="text-xs text-red-500">{formError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}